<template>
  <div class="program-index-view page">
    <div class="program-index-view__inner page__inner" :class="{'program-index-view__inner--programs': state.programs && state.programs.length < itemsPerPage}">
      <LayoutsScrollMarqueeBackground :title="state.selectedGroup || 'Programma’s'"/>

      <LayoutsPageHeaderSimple title="Programma’s">
        <ol class="program-index-view__list">
          <li v-for="(group, index) in state.programGroups">
            <AppTagTextButton :title="group.title" @click="onFilterClick(group.slug)"
                              :inverted="state.selectedGroup === group.slug"/>
          </li>
        </ol>
        <div
            class="program-index-view__content">
          <form @submit.prevent class="program-index-view__form">
            <input
                ref="inputEl"
                type="text"
                placeholder="search"
                :value="state.searchInput"
                class="program-index-view__input-el app-form-input app-form-input--searchbar"
                @keyup.enter="onFormSubmit">
            <button type="submit" class="program-index-view__button"
                    @click="onFormSubmit">
              <AppIconLoader name="search" variant="custom" aria-hidden="true" class="program-index-view__icon"/>
            </button>
          </form>
          <p v-if="state.programs && state.programs.length === 0" class="program-index-view__text">
            Er zijn geen resultaten gevonden.
          </p>
        </div>
      </LayoutsPageHeaderSimple>

      <section
          v-if="state.programs && state.programs.length !== 0"
          class="program-index-view__section"
      >
        <h2 class="program-index-view__title">{{ state.selectedGroup ? state.selectedGroup : 'Alle' }} Programma's</h2>
        <ol class="program-index-view__section-list">
          <li
              v-for="(program, index) in state.programs"
              :key="`program-card_${index}`"
              class="program-index-view__item"
          >
            <AppProgramCard :data="program" :in-slider="false"/>
          </li>
        </ol>
      </section>

      <AppPaginator
          v-show="(state.total !== 0)"
          :item-count="state.total || 0"
          :items-per-page="itemsPerPage"
          :initial-page="state.page"
          @onPageChanged="onPaginatorUpdate"
      />
    </div>
    <LayoutsFooter/>
  </div>
</template>

<script setup lang="ts">

import {onMounted, reactive, ref, watch} from 'vue'
import {useRoute, useRouter} from 'nuxt/app'
import {getCollection} from '~/services/custom-endpoints'
import {useHeadRel} from '~/composables/useHeadRel'
import {TProgramItem} from '~/types/block-types'
import {useHeadData} from '~/composables/useHeadData'
import {TPage} from '~/types/page-types'
import {TProgramGroup} from '~/types/other-types'
import {TProgramCard} from '~/types/card-types'
import {useTracking} from "~/composables/useTracking";
import {usePageStore} from "~/store/page";
import {useTheme} from "~/composables/useTheme";

const itemsPerPage = 18
const route = useRoute()
const router = useRouter()
const pageStore = usePageStore()
const inputEl = ref<HTMLInputElement>()

type TState = {
  programGroups?: TProgramGroup[]
  selectedGroup?: string,
  programs?: TProgramCard[],
  total?: number,
  searchInput: string,
  page: number,
};

const state: TState = reactive({
  programGroups: undefined,
  selectedGroup: (route.query.group) ? route.query.group as string : undefined,
  programs: undefined,
  total: undefined,
  searchInput: (route.query.search) ? route.query.search as string : '',
  page: (route.query.p) ? parseInt(route.query.p as string) : 1
})

pageStore.setCurrentPage({
  title: `${state.selectedGroup || 'Programma’s'}`,
  collection: 'generated'
})

useHeadData(pageStore.getCurrentPage as TPage)
if (import.meta.server) {
  useTheme()
}
const loadProgramGroups = async () => {
  const {data: result, pending, error} = await getCollection(
      'program_group',
      undefined,
      ['*']
  )

  if (error.value) {
    throw new Error(error.value?.message)
  }

  if (result.value?.data) {
    state.programGroups = result.value.data as TProgramGroup[]
  }
}

loadProgramGroups()

const setQueryParams = () => {
  let query
  if (state.selectedGroup || state.page || state.searchInput) {
    query = (state.selectedGroup) ? {filter: state.selectedGroup} : {}

    if (state.page > 1) {
      query.p = state.page
    }

    if (state.searchInput !== '') {
      query.search = state.searchInput
    }
  }

  router.push({
    path: router.currentRoute.value.path,
    query
  })
}

const loadProgramCards = async () => {
  state.programs = undefined

  const filter = {
    status: 'published'
  }

  if (state.selectedGroup) {
    filter.type = {
      slug: state.selectedGroup
    }
  }

  function slugify(str) {
    return String(str)
      .normalize('NFKD') // split accented characters into their base characters and diacritical marks
      .replace(/[\u0300-\u036f]/g, '') // remove all the accents, which happen to be all in the \u03xx UNICODE block.
      .trim() // trim leading or trailing whitespace
      .toLowerCase() // convert to lowercase
      .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
      .replace(/\s+/g, '-') // replace spaces with hyphens
      .replace(/-+/g, '-'); // remove consecutive hyphens
  }

  if (state.searchInput !== '') {
    filter.slug = {
      "_icontains": slugify(state.searchInput)
    }
  }

  const card_fields = [
    'id',
    'status',
    'title',
    'image',
    'slug',
    'external_url',
    'type.*',
    'domain.color.*'
  ]

  const {data: result, pending, error} = await getCollection(
      'program_page',
      filter,
      card_fields,
      ['-order', 'title'],
      itemsPerPage,
      state.page,
      true
  )

  if (result.value?.data) {
    state.total = result.value.meta.filter_count
    state.programs = result.value.data as TProgramItem[]
    setQueryParams()
  }
}

await loadProgramCards()

const onPaginatorUpdate = (pageNumber) => {
  window.scrollTo(0, 0)
  state.page = pageNumber
}

const onFilterClick = (selected) => {
  state.page = 1
  state.selectedGroup = selected === state.selectedGroup ? undefined : selected
}

const onFormSubmit = (event) => {
  event.preventDefault()
  event.stopPropagation()
  state.page = 1
  state.searchInput = inputEl.value?.value || ''
}

watch(() => state.page, loadProgramCards)
watch(() => state.selectedGroup, loadProgramCards)
watch(() => state.searchInput, loadProgramCards)

onMounted(() => {
  useTracking(pageStore.getCurrentPage)
  window.setTimeout(() => {
    useTheme()
  }, 500)
})

</script>

<style scoped lang="postcss">
.program-index-view {
  &__inner {
    &--programs {
      margin-bottom: 4rem;
    }
  }

  &__list {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;

    @media (--xxl) {
      grid-column: span 10 / span 10;
      grid-column-start: 2;
    }
  }

  &__content {
    grid-column: 1 / -1;
    margin-left: 0.75rem;
    margin-right: 0.75rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;

    @media (--md) {
      grid-column: span 10 / span 10;
      grid-column-start: 2;
    }

    @media (--lg) {
      grid-column: span 8 / span 8;
      grid-column-start: 3;
    }

    @media (--xl) {
      margin-top: 2rem;
    }

    @media (--xxl) {
      grid-column: span 6 / span 6;
      grid-column-start: 4;
    }
  }

  &__form {
    position: relative;
    display: flex;
    width: 100%;
    max-width: 42rem;
    flex-direction: column;
    justify-content: center;
    gap: 1rem;
  }

  &__input-el {
    background-color: var(--color-blue-prussian) !important;
    @mixin app-body;
  }

  &__button {
    position: absolute;
    right: 0px;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-top: 1.25rem;
    padding-bottom: 1.25rem;
  }

  &__icon {
    height: 1.25rem;
    width: 1.25rem;
  }

  &__text {
    color: #fff;
    @mixin app-caption-wide;
  }

  &__section {
    @mixin container;
    margin-bottom: 3.5rem;
    @mixin app-container-padding;
    @mixin app-grid;

    @media (--xl) {
      margin-bottom: 5rem;
    }
  }

  &__title {
    @mixin sr-only;
  }

  &__section-list {
    @mixin app-layout-overview-grid;
  }

  &__item {
    width: 100%;
  }
}

/*  */
</style>
